import { useEffect, useRef, useState } from "react";
import { Paperclip, Upload, FileText, Image as ImageIcon, X, Loader2, Download } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const ALLOWED = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
const MAX_BYTES = 10 * 1024 * 1024;

function fileIcon(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  return FileText;
}

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ActivityAttachments({ opportunityId, activityId }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [count, setCount] = useState(null);
  const inputRef = useRef(null);

  // Fetch count on mount (cheap probe)
  useEffect(() => {
    let cancel = false;
    api.get(`/opportunities/${opportunityId}/activities/${activityId}/files`)
      .then((r) => { if (!cancel) { setCount(r.data.length); setFiles(r.data); } })
      .catch(() => { if (!cancel) setCount(0); });
    return () => { cancel = true; };
  }, [opportunityId, activityId]);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get(`/opportunities/${opportunityId}/activities/${activityId}/files`);
      setFiles(data);
      setCount(data.length);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(file) {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File too large. Max 10 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/opportunities/${opportunityId}/activities/${activityId}/files`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Uploaded");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(fid) {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/files/${fid}`);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  function downloadUrl(fid) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/files/${fid}`;
  }

  return (
    <div className="mt-3" data-testid={`activity-files-${activityId}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`activity-files-toggle-${activityId}`}
        className="text-xs font-mono uppercase tracking-widest text-[#4A5568] hover:text-[#1F4E2C] inline-flex items-center gap-1.5 transition-colors"
      >
        <Paperclip className="w-3 h-3" />
        {count === null ? "Files…" : `${count} file${count === 1 ? "" : "s"}`}
        <span className="text-[#1F4E2C]">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="mt-2 border border-[#E2E4DC] bg-[#FAFAF7] p-3">
          {/* Upload row */}
          <label
            className={"block border-2 border-dashed p-3 text-center cursor-pointer transition-colors " +
              (uploading ? "border-[#1F4E2C] bg-[#1F4E2C]/5" : "border-[#E2E4DC] hover:border-[#0A1128] bg-white")}
            data-testid={`activity-upload-zone-${activityId}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED}
              className="hidden"
              disabled={uploading}
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid={`activity-file-input-${activityId}`}
            />
            <div className="flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-[#4A5568]">
              {uploading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
              ) : (
                <><Upload className="w-3.5 h-3.5" /> Click to upload (PDF · JPG · PNG · DOCX, max 10 MB)</>
              )}
            </div>
          </label>

          {/* List */}
          <div className="mt-3 space-y-1.5">
            {loading && <div className="text-xs font-mono text-[#94A3B8]">Loading…</div>}
            {!loading && files.length === 0 && (
              <div className="text-xs font-mono text-[#94A3B8] py-2 text-center">No files yet</div>
            )}
            {files.map((f) => {
              const Icon = fileIcon(f.content_type);
              return (
                <div
                  key={f.id}
                  className="flex items-center gap-2 bg-white border border-[#E2E4DC] px-2.5 py-2 text-xs"
                  data-testid={`activity-file-${f.id}`}
                >
                  <Icon className="w-4 h-4 text-[#0A1128] flex-shrink-0" />
                  <a
                    href={downloadUrl(f.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 truncate text-[#0A1128] hover:text-[#1F4E2C] hover:underline"
                  >
                    {f.filename}
                  </a>
                  <span className="font-mono text-[10px] text-[#94A3B8]">{fmtSize(f.size)}</span>
                  <span className="font-mono text-[10px] text-[#94A3B8] hidden sm:inline">
                    {f.uploaded_by_name}
                  </span>
                  <a
                    href={downloadUrl(f.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4A5568] hover:text-[#0A1128] p-1"
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(f.id)}
                    className="text-[#94A3B8] hover:text-red-600 p-1"
                    title="Delete"
                    data-testid={`activity-file-delete-${f.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
